import { useHead } from "@unhead/react";

interface SeoProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  /** Open Graph type, e.g. "website" (default) or "article" for content pages. */
  ogType?: string;
  /** JSON-LD structured data object (e.g. JobPosting schema) */
  jsonLd?: Record<string, unknown>;
}

export default function Seo({ title, description, canonical, ogImage, ogType, jsonLd }: SeoProps) {
  const BASE_URL = "https://www.bnssa-mns.fr";
  // Canonical :
  //  1) `canonical` explicite (pages qui le calculent, ex. fiche offre).
  //  2) sinon, en runtime navigateur, l'URL courante NORMALISÉE en `www.` + https,
  //     pour rester cohérent avec le domaine réellement servi (évite le mismatch
  //     `www` vs sans-`www` qui pousse Facebook à fusionner avec un autre canonical).
  //  3) en contexte serveur/prerender sans `canonical` ni `window`, on NE retombe
  //     PLUS sur la home (`BASE_URL`) : un canonical pointant vers `/` ferait croire
  //     aux crawlers (Facebook/Google) que la page est un doublon de l'accueil, et
  //     ils afficheraient le titre/OG génériques de la home. On laisse alors le
  //     canonical indéfini pour que chaque page conserve ses propres balises OG.
  const normalizeUrl = (raw: string): string => {
    try {
      const u = new URL(raw);
      u.protocol = "https:";
      if (u.hostname === "bnssa-mns.fr") u.hostname = "www.bnssa-mns.fr";
      // Neutralise le domaine de preview Lumi (*.lumi.ing) qui ne doit jamais
      // apparaître comme canonique — on le remplace systématiquement par le
      // domaine de production pour que Google n'indexe pas le domaine preview.
      if (u.hostname.endsWith(".lumi.ing")) u.hostname = "www.bnssa-mns.fr";
      return u.toString();
    } catch {
      return raw;
    }
  };
  const canonicalHref = canonical
    ? normalizeUrl(canonical)
    : (typeof window !== "undefined" ? normalizeUrl(window.location.href) : undefined);
  const ogImageHref = ogImage || "https://static.lumi.new/f1/f1bba55c139628ffc4d21310d0cc6cac.png";

  // CRITICAL SEO FIX : chaque balise reçoit une `key` de déduplication stable.
  // Sans cette clé, @unhead AJOUTE une nouvelle <meta> sans supprimer celle
  // déclarée en dur dans index.html (valeurs de la home). Les crawlers/Google
  // lisaient alors la 1re balise (home) -> meta description identique partout.
  // Avec une `key` unique, la balige de la page courante REMPLACE proprement
  // la balise existante (home incluse) sur chaque route.
  useHead({
    title,
    meta: [
      { key: "description", name: "description", content: description },
      { key: "og:title", property: "og:title", content: title },
      { key: "og:description", property: "og:description", content: description },
      { key: "og:type", property: "og:type", content: ogType || "website" },
      // og:url uniquement si un canonical fiable est connu (jamais la home par défaut).
      ...(canonicalHref ? [{ key: "og:url", property: "og:url", content: canonicalHref }] : []),
      { key: "og:site_name", property: "og:site_name", content: "Emplois BNSSA & MNS" },
      { key: "og:image", property: "og:image", content: ogImageHref },
      { key: "og:image:secure_url", property: "og:image:secure_url", content: ogImageHref },
      { key: "og:image:width", property: "og:image:width", content: "1200" },
      { key: "og:image:height", property: "og:image:height", content: "630" },
      { key: "og:image:alt", property: "og:image:alt", content: title },
      { key: "twitter:card", name: "twitter:card", content: "summary_large_image" },
      { key: "twitter:title", name: "twitter:title", content: title },
      { key: "twitter:description", name: "twitter:description", content: description },
      { key: "twitter:image", name: "twitter:image", content: ogImageHref },
      { key: "robots", name: "robots", content: "index, follow" },
    ],
    link: canonicalHref ? [{ key: "canonical", rel: "canonical", href: canonicalHref }] : [],
    script: jsonLd
      ? [{ key: "page-jsonld", type: "application/ld+json", innerHTML: JSON.stringify(jsonLd) }]
      : [],
    htmlAttrs: { lang: "fr" },
  });
  return null;
}
